import {
  audioArtifactDownloadResponseSchema,
  audioTranscriptResponseSchema,
  audioTranscriptionBatchStatusResponseSchema,
  audioTranscriptionRetryResponseSchema,
  finalizeAudioUploadResponseSchema,
  prepareAudioUploadResponseSchema,
  requestAudioUploadPartsResponseSchema,
  type FinalizeAudioUploadRequestDTO,
  type PrepareAudioUploadRequestDTO,
  type RequestAudioUploadPartsDTO,
} from '@shared/schemas/audio-transcription';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

type ApiEnvelope = {
  data?: unknown;
  error?: {
    code?: string;
    details?: Record<string, unknown>;
    message?: string;
  };
};

async function parseResponse(response: Response) {
  const payload = await response.json().catch(() => null) as ApiEnvelope | null;
  if (!response.ok || !payload) {
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? response.statusText ?? 'La requête a échoué.',
      status: response.status,
    });
  }
  return payload.data;
}

function createApiUrl(path: string) {
  return new URL(path, apiBaseUrl);
}

export async function prepareAudioUpload(input: PrepareAudioUploadRequestDTO) {
  const response = await fetch(createApiUrl('/api/chat/attachments/prepare'), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  return prepareAudioUploadResponseSchema.parse(await parseResponse(response));
}

export async function requestAudioUploadParts(attachmentId: string, input: RequestAudioUploadPartsDTO) {
  const response = await fetch(createApiUrl(`/api/chat/attachments/${attachmentId}/parts`), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  return requestAudioUploadPartsResponseSchema.parse(await parseResponse(response));
}

export async function finalizeAudioUpload(attachmentId: string, input: FinalizeAudioUploadRequestDTO) {
  const response = await fetch(createApiUrl(`/api/chat/attachments/${attachmentId}/finalize`), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  return finalizeAudioUploadResponseSchema.parse(await parseResponse(response));
}

export async function deleteChatAttachment(attachmentId: string) {
  const response = await fetch(createApiUrl(`/api/chat/attachments/${attachmentId}`), {
    credentials: 'include',
    method: 'DELETE',
  });

  if (response.status === 404) {
    return;
  }

  if (!response.ok) {
    await parseResponse(response);
  }
}

export async function getAudioTranscriptionBatch(batchId: string) {
  const response = await fetch(createApiUrl(`/api/chat/audio-transcriptions/${batchId}`), {
    credentials: 'include',
    method: 'GET',
  });
  return audioTranscriptionBatchStatusResponseSchema.parse(await parseResponse(response));
}

export async function cancelAudioTranscriptionBatch(batchId: string) {
  const response = await fetch(createApiUrl(`/api/chat/audio-transcriptions/${batchId}/cancel`), {
    credentials: 'include',
    method: 'POST',
  });
  return audioTranscriptionRetryResponseSchema.parse(await parseResponse(response));
}

export async function retryAudioTranscriptionAnalysis(batchId: string) {
  const response = await fetch(createApiUrl(`/api/chat/audio-transcriptions/${batchId}/retry-analysis`), {
    credentials: 'include',
    method: 'POST',
  });
  return audioTranscriptionRetryResponseSchema.parse(await parseResponse(response));
}

export async function retryAudioTranscriptionJob(batchId: string, jobId: string) {
  const response = await fetch(
    createApiUrl(`/api/chat/audio-transcriptions/${batchId}/jobs/${jobId}/retry`),
    {
      credentials: 'include',
      method: 'POST',
    },
  );
  return audioTranscriptionRetryResponseSchema.parse(await parseResponse(response));
}

export async function getAudioTranscript(batchId: string, jobId: string) {
  const response = await fetch(
    createApiUrl(`/api/chat/audio-transcriptions/${batchId}/jobs/${jobId}/transcript`),
    {
      credentials: 'include',
      method: 'GET',
    },
  );
  return audioTranscriptResponseSchema.parse(await parseResponse(response));
}

export async function getAudioArtifactDownload(batchId: string, artifactId: string) {
  const response = await fetch(
    createApiUrl(`/api/chat/audio-transcriptions/${batchId}/artifacts/${artifactId}/download`),
    {
      credentials: 'include',
      method: 'GET',
    },
  );
  return audioArtifactDownloadResponseSchema.parse(await parseResponse(response));
}
